<template>
    <v-dialog v-model="dialog" max-width="1500" persistent scrollable>
        <v-card>
            <v-toolbar color="primary" dark dense flat>
                <v-toolbar-title>{{ $t('digerFiyatlar') || 'Diğer Fiyatlar' }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text style="max-height: 80vh;">
                <div class="fare-select-container">
                    <!-- Genel Uçuş Bilgileri -->
                    <v-card class="flight-info-card mb-4">
                        <v-card-title class="flight-header">
                            <v-layout row align-center>
                                <!-- Kalkış -->
                                <v-flex xs12 sm6 md3 class="text-xs-center">
                                    <v-layout row wrap>
                                        <v-flex xs12 sm5 md5 row wrap>
                                            <div class="airport-code">{{ firstSegment.DepartureAirportCode }}</div>
                                            <div class="city-name">{{ firstSegment.DepartureCityName }}</div>
                                        </v-flex>
                                        <v-flex xs12 sm5 md5 row wrap>
                                            <div class="time-bold">{{ firstSegment.DepartureTime }}</div>
                                            <div class="date-small">{{ firstSegment.DepartureDate }}</div>
                                        </v-flex>
                                    </v-layout>
                                </v-flex>

                                <!-- Uçuş Bilgisi -->
                                <v-flex xs12 sm6 md6>
                                    <v-layout row wrap>
                                        <v-flex xs4 sm4 md4 class="airline-info">
                                            <img :src="getAirlineLogo(firstSegment.MarkettingAirline)"
                                                :alt="firstSegment.MarkettingAirlineName" class="airline-logo">
                                            <span class="airline-name">{{ firstSegment.MarkettingAirlineName }}</span>
                                            <span class="flight-number">{{ firstSegment.FlightNumber }}</span>
                                        </v-flex>
                                        <v-flex xs5 sm5 md5 class="journey-info">
                                            <div class="duration">{{ firstSegment.JourneyDuration }}</div>
                                            <div class="passenger-count">
                                                <v-icon small>group</v-icon>
                                                {{ totalPassengers }} {{ $t('yolcu') }}
                                            </div>
                                        </v-flex>
                                        <v-flex xs5 sm5 md5 v-if="multipleSegments" class="connection-info">
                                            <v-chip small color="orange" text-color="white">
                                                {{ flight.FlightSegments.length }} {{ $t('aktarma') }}
                                            </v-chip>
                                        </v-flex>
                                    </v-layout>
                                </v-flex>

                                <!-- Varış -->
                                <v-flex xs12 sm6 md3 class="text-xs-center">
                                    <v-layout row wrap>
                                        <v-flex xs12 sm5 md5 row wrap>
                                            <div class="time-bold">{{ lastSegment.ArrivalTime }}</div>
                                            <div class="date-small">{{ lastSegment.ArrivalDate }}</div>
                                        </v-flex>
                                        <v-flex xs12 sm5 md5 row wrap>
                                            <div class="airport-code">{{ lastSegment.ArrivalAirportCode }}</div>
                                            <div class="city-name">{{ lastSegment.ArrivalCityName }}</div>
                                        </v-flex>
                                    </v-layout>
                                </v-flex>
                            </v-layout>
                        </v-card-title>
                    </v-card>

                    <!-- Diğer Fiyatlar Listesi -->
                    <div v-if="groupedOtherPricesList && groupedOtherPricesList.length > 0" class="other-prices-section">
                        <v-layout row wrap class="section-title">
                            <v-flex :class="`pr-md-2 ${hasOtherProviders ? 'xs8' : 'xs12'}`">
                                <v-layout row wrap class="fares-container">
                                    <v-flex
                                        :class="`pr-md-2 ${hasOtherProviders ? 'xs12 sm6 md6 lg6' : 'xs12 sm6 md4 lg4'}`"
                                        v-for="(priceItem, index) in groupedOtherPricesList" :key="index" class="pa-2">
                                        <v-card class="fare-card" :class="{ selected: selectedPriceIndex === index }"
                                            @click="selectPrice(index, priceItem)" hover>
                                            <!-- Fare Header -->
                                            <v-card-title class="fare-header pa-3">
                                                <div class="fare-name-section">
                                                    <div class="fare-name">{{ getPriceName(priceItem) }}</div>
                                                    <div class="booking-class"
                                                        v-if="priceItem.SelectableFares && priceItem.SelectableFares.length > 0">
                                                        {{ $t('sinifi') }}: <strong>{{
                                                            priceItem.SelectableFares[0].BookingClass
                                                        }}</strong>
                                                    </div>
                                                </div>
                                            </v-card-title>

                                            <!-- Fare Content -->
                                            <v-card-text class="fare-content pa-3">
                                                <!-- Bagaj Bilgisi -->
                                                <div v-if="priceItem.SelectableFares && priceItem.SelectableFares.length > 0"
                                                    class="baggage-section">
                                                    <div class="baggage-title">
                                                        <v-icon small>work</v-icon>
                                                        {{ $t('bagaj') }}
                                                    </div>
                                                    <div class="baggage-details">
                                                        <span class="label">{{ $t('kabin') }}:</span>
                                                        <span class="value">
                                                            {{ priceItem.SelectableFares[0].CarryOnBaggageAllowance ?
                                                                priceItem.SelectableFares[0].CarryOnBaggageAllowance.Pieces
                                                                : '0' }}
                                                            {{
                                                                $t('adet') }}
                                                            ({{ priceItem.SelectableFares[0].CarryOnBaggageAllowance ?
                                                                priceItem.SelectableFares[0].CarryOnBaggageAllowance.Kilos :
                                                                '0' }}
                                                            kg)
                                                        </span>
                                                        <span class="label">{{ $t('bagaj') }}:</span>
                                                        <span class="value">
                                                            {{ priceItem.SelectableFares[0].CheckedBaggageAllowance ?
                                                                priceItem.SelectableFares[0].CheckedBaggageAllowance.Pieces
                                                                : '0' }}
                                                            {{
                                                                $t('adet') }}
                                                            ({{ priceItem.SelectableFares[0].CheckedBaggageAllowance ?
                                                                priceItem.SelectableFares[0].CheckedBaggageAllowance.Kilos :
                                                                '0' }}
                                                            kg)
                                                        </span>
                                                    </div>
                                                </div>

                                                <!-- Fare Tipi -->
                                                <div class="fare-type-section mb-3">
                                                    <div
                                                        v-if="priceItem.SelectableFares[0].Details && priceItem.SelectableFares[0].Details.length > 0">
                                                        <v-chip
                                                            v-for="(detail, idx) in priceItem.SelectableFares[0].Details"
                                                            :key="idx">
                                                            {{ detail }}
                                                        </v-chip>
                                                    </div>
                                                    <div
                                                        v-if="priceItem.SelectableFares[0].Penalties && priceItem.SelectableFares[0].Penalties.CancellationPenalties.length > 0">
                                                        <v-chip
                                                            v-for="(cancellation, ind) in priceItem.SelectableFares[0].Penalties.CancellationPenalties"
                                                            :key="ind">
                                                            {{ cancellation.Penalty }}
                                                        </v-chip>
                                                    </div>
                                                    <div
                                                        v-if="priceItem.SelectableFares[0].Penalties && priceItem.SelectableFares[0].Penalties.ChangePenalties.length > 0">
                                                        <v-chip
                                                            v-for="(change, ind) in priceItem.SelectableFares[0].Penalties.ChangePenalties"
                                                            :key="ind">
                                                            {{ change.Penalty }}
                                                        </v-chip>
                                                    </div>
                                                </div>
                                            </v-card-text>

                                            <!-- Fiyat Bölümü -->
                                            <div class="price-section mb-3"
                                                v-if="priceItem.SelectableFares && priceItem.SelectableFares.length > 0">
                                                <div class="total-price">
                                                    <span class="label">{{ $t('toplam') }}:</span>
                                                    <span class="price-highlight">{{
                                                        formatPrice(resolvePriceItemTotal(priceItem)) }}
                                                        {{ currency }}</span>
                                                </div>
                                            </div>

                                            <!-- Select Button -->
                                            <v-card-actions class="pa-2">
                                                <v-btn small block color="primary"
                                                    :class="{ selected: selectedPriceIndex === index }">
                                                    {{ selectedPriceIndex === index ? $t('secildi') : $t('sec') }}
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-flex>
                                </v-layout>
                            </v-flex>

                            <v-flex xs4 md4 class="pl-md-2 pa-2" v-if="hasOtherProviders">
                                <v-card class="other-providers-card elevation-1 rounded-xl">
                                    <v-card-title class="pa-4 headline">
                                        {{ $t('flight.alternatives') || 'Diğer Sağlayıcılar' }}
                                    </v-card-title>
                                    <v-card-text class="pa-4">
                                        <div v-for="(other, index) in visibleOtherProviders" :key="index"
                                            class="provider-item">
                                            <div class="provider-info">
                                                <img :height="30"
                                                    :src="getProviderLogo(other.FlightSegments[0].Provider)"
                                                    :alt="other.FlightSegments[0].Provider" />
                                                <div class="ml-3">
                                                    <div class="provider-price">
                                                        {{ currency }} {{ formatPrice(otherTotalFare(other)) }}
                                                    </div>
                                                    <div class="provider-name">{{ other.FlightSegments[0].Provider }}
                                                    </div>
                                                </div>
                                            </div>
                                            <v-btn class="buy-btn" depressed rounded small
                                                @click="selectOtherProvider(other)">
                                                Seç
                                            </v-btn>
                                        </div>

                                        <!-- Daha fazla teklif göster/gizle -->
                                        <div v-if="flight._groupOthers.length > 3"
                                            class="text-xs-center mt-3 pt-2 border-top">
                                            <v-btn text small color="grey darken-2"
                                                @click="showAllOthers = !showAllOthers" class="expand-btn text-none">
                                                <v-icon left small>{{ showAllOthers ? 'keyboard_arrow_up' :
                                                    'keyboard_arrow_down' }}</v-icon>
                                                {{ flight._groupOthers.length - 3 }} teklif daha
                                            </v-btn>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </v-flex>
                        </v-layout>
                    </div>

                    <!-- Boş Liste Mesajı -->
                    <div v-else class="empty-state">
                        <v-icon large color="grey">info</v-icon>
                        <p>{{ $t('diger_fiyat_yok') || 'Başka fiyat seçeneği bulunmamaktadır.' }}</p>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
<script>
import { VLayout } from "vuetify/lib";
import logoo from "../../../../../../assets/control";
import { resolveTotalFare, resolveFlightTotalFare, groupFaresByCode } from "./fareCurrency";

export default {
    name: "OtherPricesNew",
    props: {
        flight: {
            type: Object,
            required: true,
        },
        otherPricesList: {
            type: Array,
            required: true,
        },
        flightType: {
            type: String,
            default: "domestic", // domestic, international, cyprus
        },
        currency: {
            type: String,
            default: 'TRY',
        },
    },
    data() {
        return {
            selectedPriceIndex: 0,
            dialog: false
        };
    },
    mounted() {
        this.dialog = true;
    },
    computed: {
        firstSegment() {
            return this.flight.FlightSegments[0] || {};
        },
        lastSegment() {
            const segments = this.flight.FlightSegments;
            return segments[segments.length - 1] || {};
        },
        multipleSegments() {
            return this.flight.FlightSegments.length > 1;
        },
        totalPassengers() {
            return this.flight.AirSearchPaxItems.reduce((sum, pax) => {
                return sum + pax.PaxCount;
            }, 0);
        },
        hasOtherProviders() {
            return this.flight._groupOthers && this.flight._groupOthers.length > 0;
        },
        visibleOtherProviders() {
            if (!this.hasOtherProviders) return [];
            if (this.showAllOthers) {
                return this.flight._groupOthers;
            }
            return this.flight._groupOthers.slice(0, 3);
        },
        airlineLogos() {
            return [
                { name: 'TurkishAirlines', src: require('@/assets/images/thy.png'), active: true },
                { name: 'Ajet', src: require('@/assets/images/ajet.png'), active: true },
                { name: 'Pegasus', src: require('@/assets/images/pegasus.png'), active: true },
                { name: 'Turna', src: require('@/assets/images/turnacom.png'), active: true },
                { name: 'AirArabia', src: require('@/assets/images/airarabia.png'), active: false },
                { name: 'EasyGDS', src: require('@/assets/images/easygds.png'), active: false }
            ];
        },
        groupedOtherPricesList() {
            if (!this.otherPricesList) return [];
            return this.otherPricesList.map(priceItem => {
                if (!priceItem.SelectableFares || priceItem.SelectableFares.length === 0) {
                    return priceItem;
                }
                return { ...priceItem, SelectableFares: groupFaresByCode(priceItem.SelectableFares) };
            });
        },
    },
    methods: {
        closeDialog() {
            this.dialog = false;
            this.$emit('close');
        },
        selectPrice(index, priceItem) {
            this.selectedPriceIndex = index;
            this.$emit("select-fare", this.flight, "");
        },
        getPriceName(priceItem) {
            if (priceItem.SelectableFares && priceItem.SelectableFares.length > 0) {
                const fare = priceItem.SelectableFares[0];
                if (fare.FareName && fare.FareName !== '' && fare.FareName !== '0') {
                    return fare.FareName;
                }
                return fare.FareCode || 'Standard';
            }
            return 'Fiyat Seçeneği';
        },
        getFareTypeColor(fare) {
            const type = fare.FareInfoType;
            if (type === 'Flex') return 'green';
            if (type === 'Basic') return 'blue';
            if (type === 'Premium') return 'purple';
            return 'grey';
        },
        hasCancellationPenalty(fare) {
            if (fare.Penalties && fare.Penalties.CancellationPenalties.length > 0) {
                return fare.Penalties.CancellationPenalties[0].Amount
                    ? `${fare.Penalties.CancellationPenalties[0].Amount}`
                    : this.$t('evet');
            }
            return this.$t('hayir');
        },
        getAirlineLogo(airlineCode) {
            return logoo.logoControl(airlineCode);
        },
        getProviderLogo(provider) {
            if (!provider) return null;
            var logos = this.airlineLogos.filter(x => x.name === provider);
            if (logos.length > 0) return logos[0].src;
            return null;
        },
        formatPrice(price) {
            if (!price) return '0,00';
            // 3 497,00 formatına getirmek için
            return Number(price).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\./g, ' ');
        },
        selectOtherProvider(otherFlight, index) {
            this.$emit("select-other", otherFlight, index, true, this.flight.Id);
        },
        resolvePriceItemTotal(priceItem) {
            if (priceItem.SelectableFares && priceItem.SelectableFares.length > 0) {
                return resolveTotalFare(priceItem.SelectableFares[0], this.currency);
            }
            return 0;
        },
        otherTotalFare(other) {
            return resolveFlightTotalFare(other, this.currency);
        }
    }
}
</script>

<style scoped>
.fare-select-container {
    width: 100%;
}

/* Flight Info Card */
.flight-info-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.flight-header {
    background: transparent !important;
    color: white !important;
}

.departure-section,
.arrival-section {
    padding: 10px 0;
}

.airport-code {
    font-size: 24px;
    font-weight: bold;
    line-height: 1.2;
}

.city-name {
    font-size: 12px;
    opacity: 0.9;
    margin: 4px 0;
}

.time-bold {
    font-size: 16px;
    font-weight: 600;
    margin: 8px 0 4px 0;
}

.date-small {
    font-size: 11px;
    opacity: 0.8;
}

.airline-info {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    gap: 10px;
}

.airline-logo {
    height: 30px;
    width: 30px;
    object-fit: contain;
}

.airline-name {
    font-weight: 600;
    font-size: 13px;
}

.flight-number {
    background: rgba(255, 255, 255, 0.3);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
}

.journey-info {
    font-size: 13px;
    margin-bottom: 8px;
}

.duration {
    font-weight: 600;
}

.passenger-count {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    opacity: 0.9;
}

.connection-info {
    margin-top: 8px;
}

/* Fares Section */
.fares-container {
    min-height: 300px;
}

.fare-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.fare-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #667eea;
    transform: translateY(-4px);
}

.fare-card.selected {
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.05);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.fare-header {
    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
    border-bottom: 1px solid #f0f0f0;
    padding: 16px !important;
}

.fare-name-section {
    width: 100%;
}

.fare-name {
    font-weight: 700;
    font-size: 16px;
    color: #333;
    margin-bottom: 4px;
}

.booking-class {
    font-size: 12px;
    color: #666;
}

.fare-content {
    flex: 1;
}

/* Price Section */
.price-section {
    background: #f9f9f9;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 12px;
}

.label {
    color: #666;
    font-weight: 500;
}

.value {
    color: #333;
    font-weight: 600;
}

.total-price {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid #e0e0e0;
    margin-top: 8px;
}

.total-price .label {
    font-weight: 700;
    font-size: 14px;
}

.price-highlight {
    font-size: 18px;
    font-weight: 700;
    color: #667eea;
}

/* Baggage Section */
.baggage-section {
    background: #f0f7ff;
    padding: 10px 12px;
    border-radius: 6px;
    border-left: 3px solid #667eea;
    margin-bottom: 10px;
}

.baggage-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 12px;
    color: #333;
    margin-bottom: 8px;
}

.baggage-details {
    font-size: 12px;
}

/* Fare Type Section */
.fare-type-section {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

/* Other Providers Card (Görseldeki Tasarım) */
.other-providers-card {
    background-color: #ffffff;
    border-radius: 16px !important;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.provider-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #f0f0f0;
}

.provider-item:last-child {
    border-bottom: none;
}

.provider-info {
    display: flex;
    align-items: center;
}

.provider-price {
    font-size: 16px;
    font-weight: 700;
    color: #111;
    line-height: 1.2;
}

.provider-name {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

.buy-btn {
    background-color: #333 !important;
    color: #fff !important;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    border-radius: 20px;
}

.border-top {
    border-top: 1px solid #f0f0f0;
}

.expand-btn {
    background: transparent !important;
    font-weight: 600;
}

/* Card Actions */
.fare-card /deep/ .v-card__actions {
    background: #f9f9f9;
    border-top: 1px solid #e0e0e0;
}

.v-btn.selected {
    background: #667eea !important;
    color: white !important;
}

/* Responsive Design */
@media (max-width: 960px) {
    .airline-info {
        flex-wrap: wrap;
    }
}

@media (max-width: 600px) {
    .time-bold {
        font-size: 14px;
    }

    .airport-code {
        font-size: 20px;
    }

    .price-highlight {
        font-size: 16px;
    }
}
</style>